import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Search, Send, Save, X } from "lucide-react";
import { api, apiError, conflictMessage, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem, MotionButton, DelightBurst } from "@/components/motion";

const MENU_IMAGE_FALLBACK = "/menu-images/photo-1631452180519-c014fe946bc7.jpg";
const BACKEND_ORIGIN = API_BASE.replace(/\/api$/, "");
const LOCAL_MENU_IMAGES = {
  "photo-1432139509613-5c4255815697": "/menu-images/photo-1432139509613-5c4255815697.jpg",
  "photo-1533089860892-a7c6f0a88666": "/menu-images/photo-1533089860892-a7c6f0a88666.jpg",
  "photo-1547592180-85f173990554": "/menu-images/photo-1547592180-85f173990554.jpg",
  "photo-1548839140-29a749e1cf4d": "/menu-images/photo-1548839140-29a749e1cf4d.jpg",
  "photo-1556679343-c7306c1976bc": "/menu-images/photo-1556679343-c7306c1976bc.jpg",
  "photo-1565299624946-b28f40a0ae38": "/menu-images/photo-1565299624946-b28f40a0ae38.jpg",
  "photo-1565958011703-44f9829ba187": "/menu-images/photo-1565958011703-44f9829ba187.jpg",
  "photo-1568901346375-23c9450c58cd": "/menu-images/photo-1568901346375-23c9450c58cd.jpg",
  "photo-1571091718767-18b5b1457add": "/menu-images/photo-1571091718767-18b5b1457add.jpg",
  "photo-1574484284002-952d92456975": "/menu-images/photo-1574484284002-952d92456975.jpg",
  "photo-1576092768241-dec231879fc3": "/menu-images/photo-1576092768241-dec231879fc3.jpg",
  "photo-1581636625402-29b2a704ef13": "/menu-images/photo-1581636625402-29b2a704ef13.jpg",
  "photo-1599487488170-d11ec9c172f0": "/menu-images/photo-1599487488170-d11ec9c172f0.jpg",
  "photo-1601050690597-df0568f70950": "/menu-images/photo-1601050690597-df0568f70950.jpg",
  "photo-1606313564200-e75d5e30476c": "/menu-images/photo-1606313564200-e75d5e30476c.jpg",
  "photo-1625944525533-473f1a3d54e7": "/menu-images/photo-1625944525533-473f1a3d54e7.jpg",
  "photo-1626776877326-86a8bdac9d04": "/menu-images/photo-1599487488170-d11ec9c172f0.jpg",
  "photo-1622597467836-f3e6707e1191": "/menu-images/photo-1556679343-c7306c1976bc.jpg",
  "photo-1631452180519-c014fe946bc7": MENU_IMAGE_FALLBACK,
};

function resolveMenuImage(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("/api/")) return `${BACKEND_ORIGIN}${imageUrl}`;
  const match = imageUrl.match(/images\.unsplash\.com\/(photo-[^?]+)/);
  if (!match) return imageUrl;
  return LOCAL_MENU_IMAGES[match[1]] || MENU_IMAGE_FALLBACK;
}

export default function NewOrder() {
  const { t, tr, money, lang } = useI18n();
  const navigate = useNavigate();
  const params = useParams();
  const [search] = useSearchParams();
  const orderId = params.orderId;
  const queryTableId = search.get("tableId");

  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [tableId, setTableId] = useState(queryTableId || "");
  const [orderType, setOrderType] = useState("dine-in");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [cart, setCart] = useState([]); // {menu_item_id,name_dari,name_pashto,unit_price,quantity,notes,status?}
  const [order, setOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delightTrigger, setDelightTrigger] = useState(0);

  const loadStatic = async () => {
    try {
      const [tbl, cats, mi] = await Promise.all([
        api.get("/tables"),
        api.get("/menu/categories"),
        api.get("/menu/items"),
      ]);
      setTables(tbl.data);
      setCategories(cats.data);
      setItems(mi.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { loadStatic(); }, []);

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}`)
      .then((r) => {
        const o = r.data;
        setOrder(o);
        setTableId(o.table_id || "");
        setOrderType(o.order_type || "dine-in");
        setCustomerName(o.customer_name || "");
        setCustomerPhone(o.customer_phone || "");
        setCart(
          o.items.map((it) => ({
            menu_item_id: it.menu_item_id,
            name_dari: it.name_snapshot_dari,
            name_pashto: it.name_snapshot_pashto,
            unit_price: it.unit_price,
            quantity: it.quantity,
            notes: it.notes || "",
            status: it.status,
          })),
        );
      })
      .catch((e) => toast.error(apiError(e)));
  }, [orderId]);

  const filteredItems = useMemo(() => {
    return items
      .filter((it) => it.status === "available")
      .filter((it) => (cat === "all" ? true : it.category_id === cat))
      .filter((it) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return it.name_dari.toLowerCase().includes(q) || it.name_pashto.toLowerCase().includes(q);
      });
  }, [items, cat, query]);

  const addToCart = (mi) => {
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.menu_item_id === mi.id);
      if (idx > -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [
        ...prev,
        {
          menu_item_id: mi.id,
          name_dari: mi.name_dari,
          name_pashto: mi.name_pashto,
          unit_price: mi.price,
          quantity: 1,
          notes: "",
        },
      ];
    });
  };

  const updateQty = (mid, delta) => {
    setCart((prev) =>
      prev
        .map((c) => (c.menu_item_id === mid ? { ...c, quantity: c.quantity + delta } : c))
        .filter((c) => c.quantity > 0),
    );
  };

  const setNote = (mid, note) => {
    setCart((prev) => prev.map((c) => (c.menu_item_id === mid ? { ...c, notes: note } : c)));
  };

  const removeFromCart = (mid) => setCart((prev) => prev.filter((c) => c.menu_item_id !== mid));

  const subtotal = cart.reduce((s, c) => s + c.unit_price * c.quantity, 0);

  const saveOrder = async (sendToKitchen) => {
    if (cart.length === 0) {
      toast.error(t("emptyCart"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        table_id: tableId || null,
        order_type: orderType,
        customer_name: customerName,
        customer_phone: customerPhone,
        items: cart.map((c) => ({
          menu_item_id: c.menu_item_id,
          quantity: c.quantity,
          notes: c.notes,
        })),
      };
      let id = orderId;
      if (!id) {
        const r = await api.post("/orders", payload);
        id = r.data.id;
      } else {
        await api.put(`/orders/${id}/items`, { items: payload.items, version: order?.version });
      }
      if (sendToKitchen) {
        await api.put(`/orders/${id}/status`, { status: "sent" });
      }
      setDelightTrigger((n) => n + 1);
      toast.success(sendToKitchen ? t("sendToKitchen") + " ✓" : t("success"));
      await new Promise((r) => setTimeout(r, 180));
      navigate("/tables");
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(conflictMessage(e));
        if (e.response?.data?.detail?.order) {
          const o = e.response.data.detail.order;
          setOrder(o);
          setCart(o.items.map((it) => ({
            menu_item_id: it.menu_item_id,
            name_dari: it.name_snapshot_dari,
            name_pashto: it.name_snapshot_pashto,
            unit_price: it.unit_price,
            quantity: it.quantity,
            notes: it.notes || "",
            status: it.status,
          })));
        }
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 lg:h-[calc(100vh-9rem)]">
      {/* Menu Side (right in RTL, takes more space) */}
      <div className="flex-1 rp-card p-4 md:p-5 flex flex-col min-h-0">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="menu-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search")}
              className="w-full h-11 pr-10 pl-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#071A33]"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setCat("all")}
            className={`px-3 py-1.5 rounded-full text-sm border ${cat === "all" ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}
          >
            {t("allCategories")}
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              className={`px-3 py-1.5 rounded-full text-sm border whitespace-nowrap ${cat === c.id ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}
            >
              {tr(c, "name")}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 overflow-y-auto thin-scroll pb-2">
          <StaggerRoot className="contents" delay={0.03}>
          {filteredItems.map((mi, idx) => {
            const imageSrc = resolveMenuImage(mi.image_url);

            return (
            <StaggerItem key={mi.id} index={idx} className="contents">
            <button
              data-testid={`menu-item-${mi.id}`}
              onClick={() => addToCart(mi)}
              className="menu-item-card rp-card rp-card-hover overflow-hidden text-right flex flex-col items-stretch active:scale-95 transition-transform"
            >
              <div className="menu-item-media bg-gray-100 relative overflow-hidden">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={tr(mi, "name")}
                    loading="lazy"
                    className="menu-item-image"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = MENU_IMAGE_FALLBACK;
                    }}
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-gray-300 text-2xl">۞</div>
                )}
              </div>
              <div className="p-3">
                <div className="font-medium text-gray-900 leading-tight line-clamp-1">
                  {tr(mi, "name")}
                </div>
                <div className="text-sm font-bold text-[#071A33] tabular mt-1">
                  {money(mi.price)}
                </div>
              </div>
            </button>
            </StaggerItem>
            );
          })}
          </StaggerRoot>
          {filteredItems.length === 0 && (
            <div className="col-span-full">
              <EmptyState icon={Search} title={t("empty")} />
            </div>
          )}
        </div>
      </div>

      {/* Cart Side (left in RTL) */}
      <div className="w-full lg:w-96 rp-card p-4 md:p-5 flex flex-col min-h-0 relative">
        <DelightBurst trigger={delightTrigger} />
        <h2 className="text-lg font-bold font-heading text-gray-900 mb-3">{t("cart")}</h2>

        <div className="space-y-2 mb-3">
          <div className="grid grid-cols-3 gap-2">
            {["dine-in", "takeaway", "delivery"].map((ot) => (
              <button
                key={ot}
                data-testid={`ord-type-${ot}`}
                onClick={() => setOrderType(ot)}
                className={`py-2 rounded-lg text-xs font-medium border ${orderType === ot ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}
              >
                {t(ot === "dine-in" ? "dineIn" : ot)}
              </button>
            ))}
          </div>
          {orderType === "dine-in" && (
            <RpSelect
              testId="table-select"
              allowEmpty
              value={tableId}
              onValueChange={setTableId}
              placeholder={t("selectTable")}
              className="h-10 text-sm"
              options={tables.map((tb) => ({
                value: tb.id,
                label: `${t("table")} ${tb.number} — ${tb.section}`,
              }))}
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder={t("customerName")}
              className="h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#071A33]"
            />
            <input
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder={t("customerPhone")}
              className="h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#071A33]"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll space-y-3 -mx-2 px-2">
          {cart.length === 0 && <EmptyState title={t("emptyCart")} />}
          {cart.map((c) => (
            <div key={c.menu_item_id} className="border border-gray-100 rounded-xl p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 leading-tight">
                    {lang === "pashto" ? c.name_pashto : c.name_dari}
                  </div>
                  <div className="text-xs text-gray-500 tabular mt-0.5">{money(c.unit_price)}</div>
                </div>
                <button
                  onClick={() => removeFromCart(c.menu_item_id)}
                  className="text-gray-400 hover:text-rose-600 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 gap-2">
                <div className="flex items-center gap-1 border border-gray-200 rounded-lg">
                  <button onClick={() => updateQty(c.menu_item_id, -1)} className="p-2 hover:bg-gray-50">
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="px-2 font-medium tabular text-gray-900 min-w-[24px] text-center">{c.quantity}</span>
                  <button onClick={() => updateQty(c.menu_item_id, 1)} className="p-2 hover:bg-gray-50">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="text-sm font-bold tabular text-gray-900">
                  {money(c.unit_price * c.quantity)}
                </div>
              </div>
              <input
                value={c.notes}
                onChange={(e) => setNote(c.menu_item_id, e.target.value)}
                placeholder={t("notes")}
                className="w-full mt-2 h-9 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#071A33]"
              />
            </div>
          ))}
        </div>

        <div className="border-t border-gray-100 pt-3 mt-2 space-y-2">
          <div className="flex justify-between items-center font-bold">
            <span>{t("total")}</span>
            <span className="text-2xl tabular text-[#071A33]">{money(subtotal)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <MotionButton
              data-testid="send-to-kitchen"
              onClick={() => saveOrder(true)}
              disabled={saving || cart.length === 0}
              className="px-3 py-3 rounded-xl bg-[#071A33] hover:bg-[#7F1020] text-white text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Send className="w-4 h-4" /> {t("sendToKitchen")}
            </MotionButton>
            <MotionButton
              onClick={() => saveOrder(false)}
              disabled={saving || cart.length === 0}
              className="px-3 py-3 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {t("saveDraft")}
            </MotionButton>
          </div>
        </div>
      </div>
    </div>
  );
}
